import { DatePicker, SegmentedControl, Select } from '@/shared/components';
import type { CleaningCompletionReportResponse, CleaningScopeResponse } from '@/shared/api';
import { monthRange } from './completionReportModel';

export type ReportPeriod = 'month' | 'range';

export interface ReportSearch {
  from: string;
  to: string;
  period: ReportPeriod;
  objectId?: string;
  propertyId?: string;
  assignedToUserId?: string;
  selectedPropertyId?: string;
}

interface Props {
  search: ReportSearch;
  groups: CleaningScopeResponse[];
  report: CleaningCompletionReportResponse | undefined;
  onPatch: (patch: Partial<ReportSearch>) => void;
}

export function CompletionReportFilters({ search, groups, report, onPatch }: Props) {
  const apartments = groups
    .filter((group) => !search.objectId || group.id === search.objectId)
    .flatMap((group) =>
      group.apartments.map((apartment) => ({ ...apartment, groupName: group.name })),
    );
  const clearSelection = { selectedPropertyId: undefined };

  return (
    <section
      className="border-hairline rounded-tile @container border bg-white p-4"
      aria-label="Auswertung filtern"
    >
      {/* Schmal untereinander: neben dem Zeitraum-Schalter wären die Felder am Handy zu eng. */}
      <div className="@4xl:flex-row @4xl:items-end flex flex-col items-start gap-4">
        <SegmentedControl<ReportPeriod>
          aria-label="Zeitraum"
          value={search.period}
          onChange={(period) =>
            onPatch({
              period,
              ...(period === 'month' ? monthRange(search.from) : {}),
              ...clearSelection,
            })
          }
          options={[
            { value: 'month', label: 'Monat' },
            { value: 'range', label: 'Freier Zeitraum' },
          ]}
        />
        <div className="@md:grid-cols-2 @4xl:grid-cols-4 grid w-full min-w-0 flex-1 gap-3">
          {search.period === 'month' ? (
            <Field label="Monat">
              <DatePicker
                value={search.from}
                onChange={(day) => day && onPatch({ ...monthRange(day), ...clearSelection })}
                aria-describedby="completion-report-period-help"
              />
            </Field>
          ) : (
            <>
              <Field label="Von">
                <DatePicker
                  value={search.from}
                  onChange={(from) => from && onPatch({ from, ...clearSelection })}
                />
              </Field>
              <Field label="Bis">
                <DatePicker
                  value={search.to}
                  onChange={(to) => to && onPatch({ to, ...clearSelection })}
                  minDate={search.from}
                />
              </Field>
            </>
          )}
          <Field label="Objekt">
            <Select
              value={search.objectId ?? ''}
              onChange={(event) =>
                onPatch({
                  objectId: event.target.value || undefined,
                  propertyId: undefined,
                  ...clearSelection,
                })
              }
            >
              <option value="">Alle erlaubten Objekte</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Apartment">
            <Select
              value={search.propertyId ?? ''}
              onChange={(event) =>
                onPatch({ propertyId: event.target.value || undefined, ...clearSelection })
              }
            >
              <option value="">Alle Apartments</option>
              {apartments.map((apartment) => (
                <option key={apartment.id} value={apartment.id}>
                  {apartment.groupName}: {apartment.internalName ?? apartment.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Reinigungskraft">
            <Select
              value={search.assignedToUserId ?? ''}
              onChange={(event) =>
                onPatch({ assignedToUserId: event.target.value || undefined, ...clearSelection })
              }
            >
              <option value="">Alle Reinigungskräfte</option>
              {report?.assignees.map((person) => (
                <option key={person.userId} value={person.userId}>
                  {person.displayName}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>
      <p id="completion-report-period-help" className="text-ink-soft mt-3 text-xs">
        Gästezahlen bleiben als Stand beim Abschluss erhalten. Fehlende oder teilweise Daten werden
        nicht geschätzt.
      </p>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="text-ink-soft flex min-w-0 flex-col gap-1 text-xs font-semibold">
      {label}
      {children}
    </label>
  );
}
